import React, { useState } from "react";
import { TabPanel, Tabs } from "../../components/common/Tab";
import WithDrawRequestCard from "./WithDrawRequestCard";
import WithdrawalSearchFilter from "./WithdrawalSearchFilter";
import { Pagination } from "flowbite-react";

export default function WithDrawRequestTabs({ 
  tabsData, 
  withdrawals,
  loading,
  onStatusChange,
  currentStatus,
  handleStatusChange,
  pagination,
  searchQuery,
  onSearch,
  dateRange,
  onDateRangeChange,
  onClearFilters,
  flaggedOnly,
  onFlaggedOnlyChange
}) {
  const [isLoading, setIsLoading] = useState(false);
  const activeTabIndex = tabsData[0].cards.findIndex(tab => tab.status === currentStatus);

  const handleTabChange = async (index) => {
    const newStatus = tabsData[0].cards[index].status;
    
    // Immediately update the status to make tab switch responsive
    handleStatusChange(newStatus);
    
    // Show loading for data fetching
    setIsLoading(true);
    
    try {
      // Handle the data loading
      if (onStatusChange) {
        await Promise.resolve(onStatusChange());
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      // Add a small delay to ensure smooth transition
      setTimeout(() => {
        setIsLoading(false);
      }, 200);
    }
  };

  return (
    <div className="px-4">
      {/* Search, Filter, and Export Component */}
      <WithdrawalSearchFilter
        searchQuery={searchQuery}
        onSearch={onSearch}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
        onClearFilters={onClearFilters}
        withdrawals={withdrawals}
        currentStatus={currentStatus}
        flaggedOnly={flaggedOnly}
        onFlaggedOnlyChange={onFlaggedOnlyChange}
      />
      
      <Tabs
        activeTab={activeTabIndex}
        onTabChange={handleTabChange}
        tabStyle="flex justify-between text-center rounded-full bg-gray-100 p-1 text-sm mx-auto"
        activeClass="bg-white text-[#0E64D1] rounded-full px-4 py-2 active-box-shadow"
        inactiveClass="text-[#12131A] px-4 py-2"
      >
        {tabsData[0].cards.map((tab, index) => (
          <TabPanel 
            key={index} 
            title={tab.title}
            value={index}
          >
            <div className="relative min-h-[200px]">
              {(isLoading || loading) && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-lg">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E64D1]"></div>
                    <p className="mt-2 text-sm text-gray-600">Loading...</p>
                  </div>
                </div>
              )}
              
              {/* Empty State */}
              {!isLoading && !loading && withdrawals.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium">No withdrawal requests found</p>
                  <p className="text-sm">Try adjusting your search or filter criteria</p>
                </div>
              )}
              
              <div className={`grid grid-cols-1 md:grid-cols-2 gap-4 my-4 overflow-y-auto hide-scrollbar transition-opacity duration-200 ${(isLoading || loading) ? 'opacity-30' : 'opacity-100'}`}>
                {withdrawals.map((withdrawal, idx) => (
                  <WithDrawRequestCard 
                    key={idx} 
                    {...withdrawal}
                    onStatusChange={onStatusChange}
                    currentStatus={currentStatus}
                  />
                ))}
              </div>
              
              {/* Pagination Controls */}
              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-center text-center mx-4 mb-4">
                  <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={pagination.onPageChange}
                    showIcons={true}
                    layout="pagination"
                    theme={{
                      base: "flex items-center justify-center text-center mx-4 mb-4 w-full",
                      layout: {
                        table: {
                          base: "text-sm text-gray-700",
                          span: "font-semibold text-gray-900"
                        }
                      },
                      pages: {
                        base: "xs:mt-0 inline-flex gap-2",
                        showIcon: "inline-flex",
                        previous: {
                          base: "px-3 py-1 text-sm border rounded-lg hover:bg-gray-100",
                          icon: "h-5 w-5"
                        },
                        next: {
                          base: "px-3 py-1 text-sm border rounded-lg hover:bg-gray-100",
                          icon: "h-5 w-5"
                        },
                        selector: {
                          base: "px-3 py-1 text-sm border rounded-lg",
                          active: "bg-blue-500 text-white hover:bg-blue-600",
                          disabled: "opacity-50 cursor-normal"
                        }
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </TabPanel>
        ))}
      </Tabs>
    </div>
  );
}
